import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { projects } from '../data/mockData';
import SEO from './SEO';
import { musicAlbumSchema, bookSchema, theaterEventSchema, breadcrumbSchema } from '../utils/structuredData';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find(p => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <SEO 
          title="Project Not Found | Infinite Reyn Productions"
          noindex={true}
        />
        <div className="text-center">
          <h2 className="text-2xl mb-4">Project not found</h2>
          <button
            onClick={() => navigate('/')}
            className="text-gold hover:text-gold/80 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Generate appropriate structured data based on project category
  let projectSchema;
  if (project.category === 'music') {
    projectSchema = musicAlbumSchema(project);
  } else if (project.category === 'books') {
    projectSchema = bookSchema(project);
  } else if (project.category === 'theatre') {
    projectSchema = theaterEventSchema(project);
  }

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: project.title, url: `/project/${project.id}` }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [projectSchema, breadcrumbs]
  };

  const categoryName = project.category === 'books' ? 'Book' : 
                       project.category === 'music' ? 'Music Album' : 
                       'Theatre Production';

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`${project.title} | ${categoryName} | Infinite Reyn Productions`}
        description={project.fullDescription}
        canonical={`/project/${project.id}`}
        ogImage={project.image}
        ogType={project.category === 'music' ? 'music.album' : 'website'}
        keywords={`${project.title}, ${project.artist || project.author}, Infinite Reyn Productions, ${project.category}, ${project.status}`}
        author={project.artist || project.author || 'Infinite Reyn Productions'}
        structuredData={combinedSchema}
      />
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK</span>
      </button>

      {/* Hero Image */}
      <div className="relative h-[70vh] overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Status Badge */}
        {project.status === 'upcoming' && (
          <div className="absolute top-8 right-8 border border-gold/50 bg-black/50 backdrop-blur-sm px-4 py-2 text-xs font-light tracking-widest text-gold">
            COMING SOON
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 -mt-32 relative z-10 pb-24">
        {/* Category */}
        <div className="text-xs tracking-[0.3em] text-gold mb-4">
          {project.category.toUpperCase()}
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-6xl font-light mb-4 leading-tight">
          {project.title}
        </h1>

        {/* Artist */}
        {project.artist && (
          <p className="text-xl text-white/60 mb-8 font-light">
            {project.artist}
          </p>
        )}

        {/* Gold Divider */}
        <div className="w-24 h-px bg-gold mb-12"></div>

        {/* Description */}
        <div className="prose prose-invert prose-lg max-w-none">
          <p className="text-lg leading-relaxed text-white/80 font-light mb-8">
            {project.fullDescription}
          </p>
        </div>

        {/* Release Date */}
        {project.releaseDate && (
          <p className="text-sm text-white/40 mb-8 tracking-wider">
            {project.releaseDate}
          </p>
        )}

        {/* Tracklist for Music */}
        {project.tracks && (
          <div className="mt-12">
            <h3 className="text-sm tracking-[0.3em] text-gold mb-6">TRACKLIST</h3>
            <ol className="space-y-3">
              {project.tracks.map((track, index) => (
                <li key={index} className="flex items-baseline gap-4 text-white/60 hover:text-white transition-colors">
                  <span className="text-xs text-gold font-mono">{String(index + 1).padStart(2, '0')}</span>
                  <span className="font-light">{track}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* CTA */}
        {project.link && project.status === 'released' && (
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-12 border border-gold text-gold hover:bg-gold hover:text-black transition-all px-8 py-4 group"
          >
            <span className="tracking-wider">LISTEN ON APPLE MUSIC</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        )}

        {/* Landing Page Link for Upcoming Projects */}
        {project.landingPage && project.status === 'upcoming' && (
          <Link
            to={project.landingPage}
            className="inline-flex items-center gap-3 mt-12 border border-gold text-gold hover:bg-gold hover:text-black transition-all px-8 py-4 group"
          >
            <span className="tracking-wider">GET EARLY ACCESS</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
